import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { getFadeColor } from '@repo/toolkit/colors'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { SVGFC } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import {
  BuildingIcon,
  DeleteIcon,
  DuplicateIcon,
  MultiOptionsIcon,
  OneOptionIcon,
  PenIcon,
  TravelTwoIcon,
  TripIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import NoteBox from '@repo/ui/note-box'
import Switch from '@repo/ui/switch'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { createTripOptions } from '../../../../api/products/create-trip-options'
import { deleteJourney as deleteJourneyApi } from '../../../../api/products/delete-journey'
import { duplicateJourney } from '../../../../api/products/duplicate-journey'
import {
  deleteTripOptionsGroup,
  publishJourneys,
  publishTripOption,
  publishTripOptionGroup,
  unpublishJourneys,
  unpublishTripOption,
  unpublishTripOptionGroup,
  updateJourney as updateJourneyApi
} from '../../../../api/products/update-journey'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { useProductStore } from '../../../../lib/stores/use-product'
import { Journey, JourneyTripOptionTableData } from '../../../../lib/types/models/product/journey'
import { ProductDateProperties } from '../../../../lib/types/models/product/product-date'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import JourneyForm from '../journey-form'
import JourneyTripOptionForm from '../journey-trip-option-form'
import { mapJourneyToInput } from '../utils'
import { ChevronDownContainer } from './common'

type Props = {
  locked?: boolean
  journey: Journey
}

type DetailProps = {
  text: string
  icon?: SVGFC
}

const ProductDateJourney: React.FC<Props> = ({ locked, journey }) => {
  const [open, setOpen] = useState(true)
  const { modalState, toggleModal, closeModel } = useModalManager<
    'delete' | 'update' | 'duplicate'
  >(['delete', 'update', 'duplicate'])
  const {
    productDates,
    addJourney,
    updateJourney,
    removeJourney,
    publishJourney,
    unpublishJourney,
    addJourneyPlaces,
    removeJourneyPlacesGroup,
    publishJourneyPlacesGroup,
    unpublishJourneyPlacesGroup,
    publishJourneyPlace,
    unpublishJourneyPlace,
    groupJourneyPlaces
  } = useProductStore()

  const {
    selectedData: selectedPlace,
    modalState: placeModalState,
    toggleModal: placeToggleModal,
    closeModel: placeCloseModal
  } = useModalManager<'create' | 'delete', JourneyTripOptionTableData>(['create', 'delete'])

  const { mutate: unpublishJourneysMutation } = useMutation({
    mutationFn: unpublishJourneys,
    onError: () => publishJourney(journey.id),
    onSuccess: () => toast.success('Séjours dépubliés avec succès')
  })

  const { mutate: publishJourneysMutation } = useMutation({
    mutationFn: publishJourneys,
    onError: (error) => {
      unpublishJourney(journey.id)
      showAxiosError(error)
    },
    onSuccess: () => toast.success('Séjours publiés avec succès')
  })

  const { mutateAsync: duplicateJourneyMutation } = useMutation({
    mutationFn: duplicateJourney,
    onError: showAxiosError,
    onSuccess: (data) => {
      addJourney(journey.dateId, data)
      closeModel('duplicate')
      toast.success('Séjour dupliqué avec succès')
    }
  })

  const { mutateAsync: updateJourneyMutation } = useMutation({
    mutationFn: updateJourneyApi,
    onError: showAxiosError,
    onSuccess: (data) => {
      updateJourney(journey.id, data)
      closeModel('update')
      toast.success('Séjour modifié avec succès')
    }
  })

  const { mutate: deleteJourneyMutation, isPending: isDeleteJourneyPending } = useMutation({
    mutationFn: deleteJourneyApi,
    onError: showAxiosError,
    onSuccess: () => {
      removeJourney(journey.id)
      toggleModal('delete')
      toast.success('Séjour supprimé avec succès')
    }
  })

  const { mutateAsync: createTripOptionsMutation, isPending: isCreateTripOptionsPending } =
    useMutation({
      mutationFn: createTripOptions,
      onError: showAxiosError,
      onSuccess: (data) => {
        addJourneyPlaces(journey.id, data)
        placeCloseModal('create')
        toast.success('Lieu de séjour et voyages associés créés avec succès')
      }
    })

  const { mutateAsync: publishTripOptionGroupMutation } = useMutation({
    mutationFn: publishTripOptionGroup,
    onError: showAxiosError,
    onSuccess: () => toast.success('Voyages associés publiés avec succès')
  })

  const { mutateAsync: unpublishTripOptionGroupMutation } = useMutation({
    mutationFn: unpublishTripOptionGroup,
    onError: showAxiosError,
    onSuccess: () => toast.success('Voyages associés dépubliés avec succès')
  })

  const { mutateAsync: publishTripOptionMutation } = useMutation({
    mutationFn: publishTripOption,
    onError: showAxiosError,
    onSuccess: () => toast.success('Voyage associé publié avec succès')
  })

  const { mutateAsync: unpublishTripOptionMutation } = useMutation({
    mutationFn: unpublishTripOption,
    onError: showAxiosError,
    onSuccess: () => toast.success('Voyage associé dépublié avec succès')
  })

  const { mutateAsync: deleteMainTripMutation, isPending: isDeleteMainTripPending } = useMutation({
    mutationFn: deleteTripOptionsGroup,
    onError: showAxiosError,
    onSuccess: () => {
      if (!selectedPlace) return
      removeJourneyPlacesGroup(journey.id, selectedPlace.placeId, selectedPlace.mainTripId)
      placeCloseModal('delete')
      toast.success('Lieu de séjour et voyages associés supprimés avec succès')
    }
  })

  const productDate = productDates.find((pd) => pd.id === journey.dateId)
  const isJourneyEmpty = useMemo(() => journey.places.length === 0, [journey.places])
  const groupedJourneyTripOptions = useMemo(() => groupJourneyPlaces(journey.id), [journey.places])

  const Detail: React.FC<DetailProps> = ({ icon, text }) => {
    const renderedColor = isJourneyEmpty
      ? tailwindColors.black.DEFAULT
      : ProductDateProperties.getColor(journey.date)
    return (
      <div className="flex justify-center items-center gap-1 px-2 py-1.5">
        {icon?.({ width: '20px', height: '20px', color: renderedColor })}
        <Typography variant="tag" color={renderedColor}>
          {text}
        </Typography>
      </div>
    )
  }

  const columns: Column<JourneyTripOptionTableData>[] = [
    {
      header: 'Publié',
      accessorKey: 'published',
      cell: ({ row }) => (
        <Switch
          checked={row.original.published}
          color={tailwindColors.grey.DEFAULT}
          disabled={locked}
          onCheckedChange={() => {
            if (row.original.published) {
              unpublishJourneyPlacesGroup(
                journey.id,
                row.original.placeId,
                row.original.mainTripId
              )
              unpublishTripOptionGroupMutation({
                journeyId: journey.id,
                mainTripId: row.original.mainTripId,
                placeId: row.original.placeId
              })
            } else {
              publishJourneyPlacesGroup(journey.id, row.original.placeId, row.original.mainTripId)
              publishTripOptionGroupMutation({
                journeyId: journey.id,
                mainTripId: row.original.mainTripId,
                placeId: row.original.placeId
              })
            }
          }}
        />
      )
    },
    {
      header: 'Lieux de séjour',
      accessorKey: 'place.name',
      cell: ({ row }) => row.original.place.name
    },
    {
      header: 'Voyage principal',
      accessorKey: 'mainTrip.name',
      cell: ({ row }) => TripProperties.getTripLabel(row.original.mainTrip)
    },
    {
      header: 'Voyages secondaires ',
      accessorKey: 'secondaryTrips',
      cell: ({ row }) =>
        row.original.secondaryTrips.length === 0 ? (
          ' - '
        ) : (
          <div className="flex gap-1 flex-wrap py-1">
            {row.original.secondaryTrips.map((trip) => {
              const tripOption = journey.places.find(
                (place) =>
                  place.secondaryTrip?.id === trip.id &&
                  place.mainTrip.id === row.original.mainTripId &&
                  place.placeId === row.original.placeId
              )
              return (
                <Badge
                  key={trip.id}
                  variant={tripOption?.published ? 'primary' : 'grey'}
                  text={TripProperties.getTripLabel(trip)}
                  onClick={() => {
                    if (locked) {
                      toast.error(
                        "Vous ne pouvez pas modifier l'état de ce voyage car le séjour est publié"
                      )
                      return
                    }
                    if (!tripOption) return
                    if (tripOption.published) {
                      unpublishJourneyPlace(journey.id, tripOption.id)
                      unpublishTripOptionMutation({
                        journeyId: journey.id,
                        tripOptionId: tripOption.id
                      })
                    } else {
                      publishJourneyPlace(journey.id, tripOption.id)
                      publishTripOptionMutation({
                        journeyId: journey.id,
                        tripOptionId: tripOption.id
                      })
                    }
                  }}
                />
              )
            })}
          </div>
        )
    },
    {
      header: '',
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <ActionButton
            disabled={locked}
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              placeToggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  const journeySecondaryOptions = journey.secondaryOptions
    .map((so) => `${getTranslatedName(so.option)} ${so.price}€`)
    .join(', ')

  const journeyFaes = useMemo(() => {
    const faes = []
    if (journey.fa1eIncluded) faes.push('FA1E')
    if (journey.fa2eIncluded) faes.push('FA2E')
    return faes.join(', ')
  }, [journey])

  return (
    <div
      className={cn('border border-grey-light p-5 rounded-2xl', {
        'bg-white': open,
        'bg-white-hover': !open
      })}
    >
      <div className="flex justify-between gap-1">
        <div className="flex gap-2 items-center">
          <ChevronDownContainer
            open={open}
            color={tailwindColors.grey.lighter}
            className="bg-white-active"
            onClick={() => setOpen((prev) => !prev)}
          />
          <div
            className="flex items-center rounded flex-wrap"
            style={{
              backgroundColor: isJourneyEmpty
                ? tailwindColors.white.dark.DEFAULT
                : getFadeColor(ProductDateProperties.getColor(journey.date))
            }}
          >
            <Detail text={`Séjour ${journey.reference}`} />
            <Detail
              text={`${getTranslatedName(journey.mainOption)} - ${journey.mainOptionPrice}€`}
              icon={OneOptionIcon}
            />
            <Detail text={journeySecondaryOptions} icon={MultiOptionsIcon} />
            {journeyFaes && <Detail text={journeyFaes} icon={BuildingIcon} />}
            {(!!journey.mainOptionMinAge || !!journey.mainOptionMaxAge) && (
              <Detail
                text={
                  journey.mainOptionMinAge && journey.mainOptionMaxAge
                    ? `Âge: ${journey.mainOptionMinAge} - ${journey.mainOptionMaxAge} ans`
                    : journey.mainOptionMinAge
                      ? `Âge min : ${journey.mainOptionMinAge} ans`
                      : `Âge max : ${journey.mainOptionMaxAge} ans`
                }
              />
            )}
          </div>
        </div>

        <div>
          <div className="flex gap-2 items-center justify-end">
            <div className="border border-grey-light rounded h-10 flex justify-center items-center px-2">
              <Switch
                checked={journey.published}
                label="Publié"
                disabled={locked}
                onCheckedChange={() => {
                  if (journey.published) {
                    unpublishJourney(journey.id)
                    unpublishJourneysMutation([journey.id])
                  } else {
                    publishJourney(journey.id)
                    publishJourneysMutation([journey.id])
                  }
                }}
                labelVariant="label-medium"
                color={tailwindColors.grey.DEFAULT}
              />
            </div>
            <ActionButton
              disabled={locked}
              variant={'primary'}
              icon={DuplicateIcon}
              onClick={() => toggleModal('duplicate')}
            />
            <ActionButton
              disabled={locked}
              variant={'grey'}
              icon={PenIcon}
              onClick={() => toggleModal('update')}
            />
            <ActionButton
              disabled={locked}
              variant={'danger'}
              icon={DeleteIcon}
              onClick={() => toggleModal('delete')}
            />
          </div>
        </div>
      </div>

      {open && (
        <div>
          <div className="h-2" />
          <Typography variant="caption">Voyages et lieux de séjour</Typography>
          <div className="h-3" />

          <DataTable
            createText={conditionalRender(
              !locked,
              'Ajouter un lieu de séjour et associer des voyages'
            )}
            headerClassName="bg-white-dark"
            columns={columns}
            data={groupedJourneyTripOptions}
            rowCount={groupedJourneyTripOptions.length}
            paginated={false}
            disableHover
            createOnClick={() => placeToggleModal('create')}
          />
        </div>
      )}

      <Modal
        actionButtonsOptions={{
          confirmText: 'Supprimer',
          cancelText: 'Fermer',
          onConfirm: () => {
            deleteJourneyMutation(journey.id)
          }
        }}
        onCancel={() => closeModel('delete')}
        isOpen={modalState.delete}
        title={`Êtes-vous sûr de vouloir supprimer le séjour '${journey.reference}' ?`}
        icon={DeleteIcon}
        isLoading={isDeleteJourneyPending}
        variant={'destructive'}
        hasBorderBottom={false}
      >
        <div className={MODAL_BODY_CLASSNAME}>
          <Typography variant="caption" className="text-center" color={tailwindColors.red.DEFAULT}>
            Vous êtes sur le point de supprimer le séjour. Cela va supprimer tous les voyages et
            lieux de séjour associés
          </Typography>
        </div>
      </Modal>

      {selectedPlace && (
        <Modal
          actionButtonsOptions={{
            confirmText: 'Supprimer',
            cancelText: 'Fermer',
            onConfirm: () => {
              deleteMainTripMutation({
                id: journey.id,
                mainTripId: selectedPlace.mainTripId,
                placeId: selectedPlace.placeId
              })
            }
          }}
          onCancel={() => placeCloseModal('delete')}
          isOpen={placeModalState.delete}
          title={`Etes-vous sûr de vouloir supprimer ces lieux?`}
          icon={DeleteIcon}
          isLoading={isDeleteMainTripPending}
          variant={'destructive'}
        />
      )}

      <Modal
        isOpen={modalState.update}
        title={'Modifier le séjour'}
        icon={TravelTwoIcon}
        size="large"
        hideActions
        onCancel={() => closeModel('update')}
      >
        <JourneyForm
          productDateId={journey.dateId}
          periodName={journey.date.period?.name}
          className={MODAL_BODY_CLASSNAME}
          defaultValues={mapJourneyToInput(journey)}
          savedOptions={[
            journey.mainOption,
            ...journey.secondaryOptions.map(({ option }) => option)
          ]}
          buttonsConfig={modalButtonConfig({
            confirmText: 'Modifier',
            onCancel: () => closeModel('update'),
            onConfirm: async ({ isMandatoryUnderAge: _isMandatoryUnderAge, ...values }) => {
              await updateJourneyMutation({
                ...values,
                id: journey.id,
                productDateId: journey.dateId
              })
            }
          })}
        />
      </Modal>

      {productDate && (
        <Modal
          isOpen={modalState.duplicate}
          title={'Dupliquer le séjour'}
          headerChildren={
            <Badge
              text={`${DateFormatter.DDMMYYYY(productDate.departureDate)} - ${DateFormatter.DDMMYYYY(productDate.returnDate)}`}
              variant="grey"
              size="small"
              textVariant="caption"
            />
          }
          icon={TravelTwoIcon}
          size="large"
          hideActions
          onCancel={() => closeModel('duplicate')}
        >
          <div className={MODAL_BODY_CLASSNAME}>
            <NoteBox note="Vous êtes sur le point de dupliquer le séjour. Cela va également dupliquer les voyages associés." />
          </div>
          <JourneyForm
            productDateId={journey.dateId}
            periodName={journey.date.period?.name}
            className={MODAL_BODY_CLASSNAME}
            defaultValues={mapJourneyToInput(journey)}
            buttonsConfig={modalButtonConfig({
              confirmText: 'Dupliquer',
              onCancel: () => closeModel('duplicate'),
              onConfirm: async ({ isMandatoryUnderAge: _isMandatoryUnderAge, ...values }) => {
                await duplicateJourneyMutation({
                  ...values,
                  id: journey.id,
                  productDateId: journey.dateId
                })
              }
            })}
          />
        </Modal>
      )}

      <Modal
        isOpen={placeModalState.create}
        title={'Ajouter un lieu de séjour et associer des voyages'}
        icon={TripIcon}
        size="large"
        hideActions
        onCancel={() => placeCloseModal('create')}
      >
        <JourneyTripOptionForm
          className={MODAL_BODY_CLASSNAME}
          country={journey.date.product.country}
          accomodationCategory={journey.date.product.accomodationCategory}
          isLoading={isCreateTripOptionsPending}
          handleSubmit={(values) =>
            createTripOptionsMutation({ ...values, journeyId: journey.id })
          }
          defaultValues={{
            secondaryTrips: []
          }}
          onCancel={() => placeCloseModal('create')}
        />
      </Modal>
    </div>
  )
}

export default ProductDateJourney
